import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { connectRefinementList } from 'react-instantsearch-dom';
import Typography from '@material-ui/core/Typography';
import Icon from '@material-ui/core/Icon';
import Popover from '@material-ui/core/Popover';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import filterStyles from '../../../theme/filterStyles';

function CarCategoryFilter({ refine, currentRefinement }) {
  const classes = useStyles();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (value) => {
    if (currentRefinement.includes(value)) {
      refine(currentRefinement.filter(item => item !== value));
    } else {
      refine([...currentRefinement, value]);
    }
    if (isMobile) {
      setAnchorEl(null);
    }
  };

  const handleReset = () => {
    refine([]);
    if (isMobile) {
      setAnchorEl(null);
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const isActive = (value) => currentRefinement.includes(value);

  const getCurrentLabel = () => {
    if (currentRefinement.length === 0) return 'Toate';
    if (currentRefinement.length === 1) {
      switch (currentRefinement[0]) {
        case 'Auto noi': return 'Auto noi';
        case 'Auto rulate': return 'Auto rulate';
        case 'Ecobonus': return 'Eco bonus';
        case 'Oferte speciale': return 'Oferte speciale';
        default: return 'Toate';
      }
    }
    return `${currentRefinement.length} selectate`;
  };

  const renderFormControls = (value, label) => {
    // Special handling for "Toate" (reset all)
    if (value === '') {
      return (
        <FormControlLabel
          className={classes.label}
          classes={{ label: classes.smallMobileLabel }}
          label={label}
          key={value}
          control={
            <Checkbox
              checked={currentRefinement.length === 0}
              onChange={handleReset}
              color={'primary'}
              checkedIcon={<div className={`${classes.rounded} ${classes.customCheckbox}`}> </div>}
              icon={<div className={`${classes.rounded} ${classes.emptyCustomCheckbox}`}> </div>}
            />
          }
        />
      );
    }

    return (
      <FormControlLabel
        className={classes.label}
        classes={{ label: classes.smallMobileLabel }}
        label={label}
        key={value}
        control={
          <Checkbox
            checked={isActive(value)}
            onChange={() => handleClick(value)}
            color={'primary'}
            checkedIcon={<div className={`${classes.rounded} ${classes.customCheckbox}`}> </div>}
            icon={<div className={`${classes.rounded} ${classes.emptyCustomCheckbox}`}> </div>}
          />
        }
      />
    );
  };

  if (isMobile) {
    return (
      <Box className={classes.root}>
        <Button
          size="small"
          variant="outlined"
          aria-describedby="categoryToggle"
          onClick={handleMenuOpen}
          className={`${classes.sortButton} ${classes.categoryWidth} ${Boolean(anchorEl) ? classes.openSortButtonStyle : ''}`}
        >
          <Typography variant={"body1"} className={`${classes.weight300} ${classes.sortButtonText}`}>
            {getCurrentLabel()}
          </Typography>
          <Icon className={`${classes.sortIcon} brand-icon-down-arrow`} color={"primary"} />
        </Button>

        <Popover
          PaperProps={{
            elevation: 0,
            classes: { root: `${classes.categoryWidth} ${classes.popoverPaper}` }
          }}
          id='categoryToggle'
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleMenuClose}
        >
          <Box display={"flex"} flexDirection="column">
            {renderFormControls('', 'Toate')}
            {renderFormControls('Auto noi', 'Auto noi')}
            {renderFormControls('Auto rulate', 'Auto rulate')}
            {renderFormControls('Ecobonus', 'Eco bonus')}
            {renderFormControls('Oferte speciale', 'Oferte speciale')}
          </Box>
        </Popover>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      <Button
        className={`${classes.button} ${currentRefinement.length === 0 ? classes.activeButton : ''}`}
        onClick={handleReset}
      >
        Toate
      </Button>
      <Button
        className={`${classes.button} ${isActive('Auto noi') ? classes.activeButton : ''}`}
        onClick={() => handleClick('Auto noi')}
      >
        Auto noi
      </Button>
      <Button
        className={`${classes.button} ${isActive('Auto rulate') ? classes.activeButton : ''}`}
        onClick={() => handleClick('Auto rulate')}
      >
        Auto rulate
      </Button>
      <Button
        className={`${classes.button} ${isActive('Ecobonus') ? classes.activeButton : ''}`}
        onClick={() => handleClick('Ecobonus')}
      >
        Eco bonus
      </Button>
      <Button
        className={`${classes.button} ${isActive('Oferte speciale') ? classes.activeButton : ''}`}
        onClick={() => handleClick('Oferte speciale')}
      >
        Oferte speciale
      </Button>
    </Box>
  );
}

export default connectRefinementList(CarCategoryFilter);

const useStyles = makeStyles((theme) => ({
  ...filterStyles(theme),
  categoryWidth: {
    [theme.breakpoints.down('sm')]: {
      width: `calc(100vw - ${theme.spacing(6)})`
    },
    width: 210
  },
  root: {
    display: 'flex',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      marginBottom: theme.spacing(3),
    },
  },
  button: {
    flex: 1,
    borderRadius: theme.spacing(3),
    textTransform: 'none',
    fontSize: 16,
    lineHeight: '24px',
    padding: theme.spacing(0.75),
    minWidth: 124,
    backgroundColor: '#f7f7f7',
    color: '#333',
    fontWeight: 400,
    '&:hover': {
      backgroundColor: '#e0e0e0',
    },
  },
  activeButton: {
    backgroundColor: '#4B7CC4 !important',
    color: '#fff !important',
    '&:hover': {
      backgroundColor: '#3d63a4 !important',
    },
  },
  mobileButton: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    '&:hover': {
      backgroundColor: '#fff',
    },
  },
  chevronIcon: {
    color: '#4B7CC4',
    fontSize: 24,
  },
  popoverPaper: {
    border: `1px solid #eee`,
    borderTopWidth: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(2)
  },
  label: {
    margin: 0
  },
  smallMobileLabel: {
    '@media (max-width: 460px)': {
      fontSize: `0.8rem`
    },
  },

  menuItem: {
    minWidth: 200,
    '&.selected': {
      backgroundColor: '#4B7CC4',
      color: '#fff',
      '&:hover': {
        backgroundColor: '#3d63a4',
      },
    },
  },
}));
